import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { UserPlus, X } from "lucide-react";
import UserPickerDialog from "./UserPickerDialog";
import { useUsers } from "../hooks/useUsers";
// AuthContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useAuth } from "../../../contexts/AuthContext";
import type { MCPSharingUserItem } from "../../../services/agent-tools-service";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/cn";

type Props = {
  value: MCPSharingUserItem[];
  onChange: (users: MCPSharingUserItem[]) => void;
  requireCurrentUser?: boolean;
  triggerClassName?: string;
  isFocusOwnerCurrent?: () => boolean;
};

const UserMultiSelect = ({
  value,
  onChange,
  requireCurrentUser = false,
  triggerClassName,
  isFocusOwnerCurrent,
}: Props) => {
  const { t } = useTranslation();
  const userQuery = useUsers();
  const { data: users = [], isLoading } = userQuery;
  const { userProfile, currentUser } = useAuth();
  const [pickerOpen, setPickerOpen] = useState(false);
  const dialogId = useId();
  const [opening, setOpening] = useState<{
    id: number;
    trigger: HTMLButtonElement;
    parent: HTMLElement | null;
  } | null>(null);
  const focusOwner = useRef(opening);
  useLayoutEffect(() => {
    if (pickerOpen) focusOwner.current = opening;
  }, [pickerOpen, opening]);
  useLayoutEffect(() => () => { focusOwner.current = null; }, []);

  const restorePickerFocus = (event: Event) => {
    event.preventDefault();
    if (!opening || focusOwner.current !== opening ||
      (isFocusOwnerCurrent && !isFocusOwnerCurrent())) return;
    const parent = opening.parent;
    if (parent && (!parent.isConnected || parent.dataset.state !== "open")) return;
    // The parent dialog is expected to remain open; only another modal vetoes
    // restoration. Never use a global selector to choose a focus target.
    if (Array.from(document.querySelectorAll('[role="dialog"][data-state="open"]'))
      .some((dialog) => dialog !== parent)) return;
    for (const candidate of [opening.trigger, parent]) {
      if (!candidate?.isConnected || (parent && !parent.contains(candidate)) ||
        candidate.matches(":disabled, [aria-disabled='true']") ||
        candidate.closest("[hidden], [inert], [aria-hidden='true']")) continue;
      const style = getComputedStyle(candidate);
      if (style.display === "none" || style.visibility === "hidden" || style.visibility === "collapse") continue;
      candidate.focus();
      if (document.activeElement === candidate) break;
    }
  };

  // Names resolve against the full member list — a connection shared with the
  // signed-in user before this filter existed should still show their name.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of users) map.set(user.id, user.username);
    return map;
  }, [users]);

  // Sharing with yourself is meaningless: you already own what you create, and
  // some orgs return the signed-in user among their members while others do
  // not, so the option showed up inconsistently.
  //
  // Match on email first: the auth profile carries the Keycloak subject id,
  // which is a different uuid from the org member's `user.id`, so an id
  // comparison alone never matched. The id check stays as a fallback for
  // tokens issued without an email claim.
  const selfId: string | undefined = userProfile?.uid ?? currentUser?.id;
  const selfEmail: string | undefined = (
    userProfile?.email ?? currentUser?.email
  )?.toLowerCase();

  // The auth subject id and organization user id are not guaranteed to be the
  // same. Resolve the creator against the organization member list so the
  // sharing payload uses the id expected by the MCP API.
  const currentOrgUser = useMemo(() => {
    const byEmail = selfEmail
      ? users.find((user) => user.email?.toLowerCase() === selfEmail)
      : undefined;
    return byEmail ?? users.find((user) => selfId && user.id === selfId);
  }, [users, selfEmail, selfId]);

  const includeRequiredOwner = useCallback(
    (next: MCPSharingUserItem[]) => {
      if (!requireCurrentUser || !currentOrgUser) return next;
      return [
        { id: currentOrgUser.id, role: "owner" },
        ...next.filter((user) => user.id !== currentOrgUser.id),
      ];
    },
    [currentOrgUser, requireCurrentUser],
  );

  // Creating a user-scoped MCP must show its owner before save. Previously the
  // owner only appeared after the backend normalized the saved connection.
  useEffect(() => {
    if (!requireCurrentUser || !currentOrgUser) return;
    const ownerEntries = value.filter((user) => user.id === currentOrgUser.id);
    if (ownerEntries.length === 1 && ownerEntries[0].role === "owner") return;
    onChange(includeRequiredOwner(value));
  }, [
    currentOrgUser,
    includeRequiredOwner,
    onChange,
    requireCurrentUser,
    value,
  ]);

  const selectableUsers = useMemo(
    () =>
      users.filter((user) => {
        if (selfEmail && user.email?.toLowerCase() === selfEmail) return false;
        return !(selfId && user.id === selfId);
      }),
    [users, selfId, selfEmail],
  );

  const summary = isLoading
    ? t("mcpPage.loadingUsers")
    : value.length === 0
      ? t("mcpPage.selectUsers")
      : t("mcpPage.usersDialog.selectedCount", { count: value.length });

  return (
    <>
      <button
        type="button"
        aria-label={t("mcpPage.fields.users")}
        aria-haspopup="dialog"
        aria-expanded={pickerOpen}
        aria-controls={pickerOpen ? dialogId : undefined}
        onClick={(event) => {
          const trigger = event.currentTarget;
          const parent = trigger.closest<HTMLElement>('[role="dialog"]');
          setOpening((previous) => ({ id: (previous?.id ?? 0) + 1, trigger, parent }));
          setPickerOpen(true);
        }}
        className={cn(
          "flex h-10 w-full items-center justify-between gap-2 rounded-[8px] border border-border bg-card px-3 text-sm outline-none hover:border-border/80 focus-visible:ring-3 focus-visible:ring-ring/50",
          triggerClassName,
        )}
      >
        <span
          className={
            value.length === 0 ? "text-muted-foreground" : "text-foreground"
          }
        >
          {summary}
        </span>
        <UserPlus className="size-4 shrink-0 text-muted-foreground" />
      </button>

      {/* Selections wrap instead of scrolling sideways, and each chip carries
          its role so the assignment is visible without reopening the picker. */}
      {value.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {value.map((user) => {
            const name = nameById.get(user.id) ?? user.id;
            const isRequiredOwner =
              requireCurrentUser && user.id === currentOrgUser?.id;
            return (
              <li
                key={user.id}
                className="flex items-center gap-1.5 rounded-md bg-muted px-2 py-0.5 text-xs text-foreground"
              >
                <span>{name}</span>
                <span className="text-muted-foreground">
                  {t(`mcpPage.userRoles.${user.role}`)}
                </span>
                {!isRequiredOwner && (
                  <button
                    type="button"
                    onClick={() =>
                      onChange(value.filter((item) => item.id !== user.id))
                    }
                    aria-label={t("mcpPage.actions.remove", { name })}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="size-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <UserPickerDialog
        // Freeze each closing callback to its own opening, even if a new
        // picker opens before Radix delivers the previous close autofocus.
        key={opening?.id ?? 0}
        contentId={dialogId}
        onCloseAutoFocus={restorePickerFocus}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        users={selectableUsers}
        isLoading={isLoading}
        isPending={userQuery.isPending}
        isError={userQuery.isError}
        isFetching={userQuery.isFetching}
        canUseData={userQuery.canUseData}
        onRetry={userQuery.retry}
        validateData={userQuery.validateData}
        value={value}
        onConfirm={(next) => onChange(includeRequiredOwner(next))}
      />
    </>
  );
};

export default UserMultiSelect;
